import { useCallback, useEffect, useRef } from 'react';

// ============================================================================
// USE AUTO ACCEPT FAILURE SOUND - OTO ONAY/YAZDIRMA HATA ALARMI (Web Audio API)
// ============================================================================

/** Alarm kaç kez tekrar etsin (sonsuz çalıp ekranı sağır etmesin). */
const MAX_REPEATS = 14;
/** Tekrarlar arası bekleme (ms). MAX_REPEATS ile birlikte ~20 sn alarm eder. */
const REPEAT_INTERVAL_MS = 1400;

export interface UseAutoAcceptFailureSoundReturn {
  /** Hata alarmını çal (baştan başlatır). */
  playFailureSound: () => void;
  /** Alarmı durdur (kullanıcı uyarıyı onayladığında). */
  stopFailureSound: () => void;
}

/**
 * Oto kabul + yazdırma başarısız olduğunda çalan alarm.
 *
 * `useNewOrderSound`tan KASITLI olarak farklı bir karakterde: yeni sipariş sesi
 * yüksek frekanslı, neşeli `square` bip üçlüsüdür; bu ise alçak frekanslı,
 * aşağı doğru kayan sert bir `sawtooth` vızıltısıdır. Kasiyer sesi duyunca
 * "yeni sipariş" ile "işlem başarısız" ayrımını ekrana bakmadan yapabilir.
 *
 * Modal kullanıcı onaylayana kadar açık kalır; ses ise MAX_REPEATS sonunda
 * kendiliğinden susar (gece boyu boş dükkânda çalmasın).
 */
export const useAutoAcceptFailureSound = (): UseAutoAcceptFailureSoundReturn => {
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const repeatCountRef = useRef(0);

  const stopFailureSound = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    repeatCountRef.current = 0;

    if (audioContextRef.current) {
      try {
        audioContextRef.current.close();
      } catch (e) {
        // Zaten kapalı olabilir
      }
      audioContextRef.current = null;
    }
  }, []);

  const playFailureSound = useCallback(() => {
    stopFailureSound();

    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      /**
       * İki adet alçalan vızıltı (300Hz → 140Hz). Sert `sawtooth` dalga +
       * frekans kayması "bir şeyler ters gitti" hissi verir.
       */
      const playFailurePattern = () => {
        const ctx = audioContextRef.current;
        if (!ctx) return;

        const now = ctx.currentTime;
        const sweeps = [
          { start: 0, duration: 0.38 },
          { start: 0.48, duration: 0.38 },
        ];

        sweeps.forEach(({ start, duration }) => {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);

          oscillator.type = 'sawtooth';
          oscillator.frequency.setValueAtTime(300, now + start);
          oscillator.frequency.exponentialRampToValueAtTime(140, now + start + duration);

          gainNode.gain.setValueAtTime(0, now + start);
          gainNode.gain.linearRampToValueAtTime(0.3, now + start + 0.03);
          gainNode.gain.setValueAtTime(0.3, now + start + duration - 0.05);
          gainNode.gain.linearRampToValueAtTime(0, now + start + duration);

          oscillator.start(now + start);
          oscillator.stop(now + start + duration);
        });
      };

      playFailurePattern();
      repeatCountRef.current = 1;

      intervalIdRef.current = setInterval(() => {
        if (repeatCountRef.current >= MAX_REPEATS) {
          stopFailureSound();
          return;
        }
        repeatCountRef.current += 1;
        playFailurePattern();
      }, REPEAT_INTERVAL_MS);
    } catch (error) {
      console.error('🔔 [AutoAcceptFailureSound] Ses çalınamadı:', error);
    }
  }, [stopFailureSound]);

  useEffect(() => stopFailureSound, [stopFailureSound]);

  return { playFailureSound, stopFailureSound };
};

export default useAutoAcceptFailureSound;
